// import { DispatcherResponse, CreateDispatcherData, UpdateDispatcherData } from './dispatcher.model';
import { BaseQueryParams } from '../../Global.model';
import { twApi } from '../api';
import { CarrierResponse, CreateCarrierData, CreateCarrierParams } from './carriers.model';

/**
 * Api endpoints for carriers
 */
const carrierApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getCarriers: builder.query<{ content: CarrierResponse[]; total: number }, BaseQueryParams>({
      // Provides a list of `Carriers` by `id`.
      // If any mutation is executed that `invalidate`s any of these tags, this query will re-run to be always up-to-date.
      // The `CARRIERS_LIST` id is a "virtual id" we just made up to be able to invalidate this query specifically if a new `Carriers` element was added.
      query: (arg) => ({
        url: 'contacts/carriers',
        params: { ...arg }
      }),
      transformResponse: (response: { data: CarrierResponse[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result) =>
        result?.content
          ? // successful query
            [
              ...result.content.map(({ id }) => ({ type: 'Carriers', id } as const)),
              { type: 'Carriers', id: 'CARRIERS_LIST' }
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Carriers', id: 'CARRIERS_LIST' }` is invalidated
            [{ type: 'Carriers', id: 'CARRIERS_LIST' }]
    }),
    getCarrierById: builder.query<CarrierResponse, string>({
      query: (id) => ({
        url: `contacts/carriers/${id}`
      }),
      transformResponse: (response: { data: CarrierResponse }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'Carriers' as const, id: id }]
    }),
    createCarrier: builder.mutation<CarrierResponse, CreateCarrierData>({
      query: (carrier) => {
        const {
          name,
          email,
          phone,
          payRate,
          defaultPayRateType,
          mcNumber,
          taxId,
          externalCompanyId,
          payTo,
          pocFirstname,
          pocLastname,
          locationId,
          createUserAndSendInvite
        } = carrier;

        const params: CreateCarrierParams = {
          name: name
        };

        !!email ? (params.email_address = email) : null;
        !!phone ? (params.phone = phone.toString()) : null;
        !!mcNumber ? (params.mc_number = mcNumber) : null;
        !!taxId ? (params.tax_id = taxId) : null;
        !!externalCompanyId ? (params.external_company_id = externalCompanyId) : null;
        !!payTo ? (params.pay_to = payTo) : null;
        !!pocFirstname ? (params.poc_first_name = pocFirstname) : null;
        !!pocLastname ? (params.poc_last_name = pocLastname) : null;
        !!locationId ? (params.location_id = locationId) : null;
        if (defaultPayRateType) params.default_pay_rate_type = defaultPayRateType;
        if (payRate) params.pay_rate = payRate;
        if (createUserAndSendInvite) params.create_user_and_send_invite = createUserAndSendInvite;

        return {
          url: 'contacts/carriers',
          method: 'POST',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: CarrierResponse }) => response.data,
      invalidatesTags: [{ type: 'Carriers', id: 'CARRIERS_LIST' }]
    }),
    updateCarrier: builder.mutation<CarrierResponse, CreateCarrierData & { id: string }>({
      query: (carrierData) => {
        const {
          name,
          email,
          phone,
          payRate,
          defaultPayRateType,
          mcNumber,
          taxId,
          externalCompanyId,
          payTo,
          pocFirstname,
          pocLastname,
          id,
          locationId,
          phone_ext,
          notes
        } = carrierData;

        const params: CreateCarrierParams = {
          name: name,
          phone_ext: !!phone_ext ? phone_ext : null,
          location_id: !!locationId ? locationId : null,
          mc_number: !!mcNumber ? mcNumber : null,
          tax_id: !!taxId ? taxId : null,
          external_company_id: !!externalCompanyId ? externalCompanyId : null,
          pay_to: !!payTo ? payTo : null
        };

        !!email ? (params.email_address = email) : null;
        !!phone ? (params.phone = phone.toString()) : null;
        !!pocFirstname ? (params.poc_first_name = pocFirstname) : null;
        !!pocLastname ? (params.poc_last_name = pocLastname) : null;
        if (defaultPayRateType) params.default_pay_rate_type = defaultPayRateType;
        if (payRate) params.pay_rate = payRate;
        if (notes !== undefined && notes !== null) params.notes = notes;

        return {
          url: `contacts/carriers/${id}`,
          method: 'PUT',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: CarrierResponse }) => response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Carriers', id },
        { type: 'Carriers', id: 'CARRIERS_LIST' }
      ]
    }),
    deleteCarrier: builder.mutation({
      query: (id: string) => ({
        url: `contacts/carriers/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          carrierApi.util.updateQueryData('getCarriers', {}, (carriersData) => {
            const index = carriersData.content.findIndex((c) => c.id === id);
            delete carriersData.content[index];
            carriersData.total -= 1;
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Carriers', id },
        { type: 'Carriers', id: 'CARRIERS_LIST' }
      ]
    })
  }),
  overrideExisting: false
});

export const {
  useGetCarriersQuery,
  useGetCarrierByIdQuery,
  useCreateCarrierMutation,
  useUpdateCarrierMutation,
  useDeleteCarrierMutation
} = carrierApi;
